import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';

export interface CommunicationTemplate {
  id: string;
  name: string;
  content: string;
  type: 'email' | 'call';
  leadStatus?: string; // Associated lead status (new, qualified, etc.)
  category?: string;    // Custom categories (follow-up, introduction, etc.)
  tags?: string[];      // For better organization
  isDefault?: boolean;  // Whether this is a default template
  companyId?: string;   // Company ID the template belongs to
  createdBy: string;
  createdAt: Date | string;
  updatedAt: Date | string;
  usageCount?: number;  // Track how many times the template was used
  leadId?: string;      // Add the new leadId field
}

// Map database fields to our interface
const mapDbToTemplate = (data: any): CommunicationTemplate => {
  return {
    id: data.id,
    name: data.name,
    content: data.content,
    type: data.type,
    leadStatus: data.lead_status,
    category: data.category,
    tags: data.tags,
    isDefault: data.is_default,
    companyId: data.company_id,
    createdBy: data.created_by,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    usageCount: data.usage_count,
    leadId: data.lead_id
  };
};

// Map our interface to database fields
const mapTemplateToDb = (template: Partial<CommunicationTemplate>) => {
  return {
    id: template.id,
    name: template.name,
    content: template.content,
    type: template.type,
    lead_status: template.leadStatus,
    category: template.category,
    tags: template.tags,
    is_default: template.isDefault,
    company_id: template.companyId,
    created_by: template.createdBy,
    created_at: template.createdAt instanceof Date ? template.createdAt.toISOString() : template.createdAt,
    updated_at: template.updatedAt instanceof Date ? template.updatedAt.toISOString() : template.updatedAt,
    usage_count: template.usageCount,
    lead_id: template.leadId
  };
};

/**
 * Get all templates by type
 */
export const getTemplatesByType = async (
  type: 'email' | 'call',
  companyId?: string,
  leadId?: string
): Promise<CommunicationTemplate[]> => {
  try {
    let query = supabase
      .from('communication_templates')
      .select('*')
      .eq('type', type);
    
    if (companyId) {
      query = query.eq('company_id', companyId);
    }
    
    if (leadId) {
      query = query.or(`lead_id.is.null,lead_id.eq.${leadId}`);
    }
    
    const { data, error } = await query.order('updated_at', { ascending: false });
    
    if (error) {
      console.error(`Error fetching ${type} templates:`, error);
      return [];
    }
    
    return data ? data.map(mapDbToTemplate) : [];
  } catch (error) {
    console.error(`Error in getTemplatesByType (${type}):`, error);
    return [];
  }
};

/**
 * Save a new template or update an existing one
 */
export const saveTemplate = async (
  template: Partial<CommunicationTemplate>
): Promise<CommunicationTemplate | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    
    const now = new Date().toISOString();
    
    const templateToSave = {
      ...template,
      updatedAt: now,
      createdBy: user.id,
    };
    
    // If it's a new template, add ID and creation date
    if (!template.id) {
      templateToSave.id = uuidv4();
      templateToSave.createdAt = now;
    } else {
      // For updates, explicitly preserve the creation date if it exists
      if (template.createdAt) {
        templateToSave.createdAt = template.createdAt;
      }
    }
    
    const dbData = mapTemplateToDb(templateToSave);
    
    // Remove undefined fields to avoid overwriting with nulls
    Object.keys(dbData).forEach(key => {
      if (dbData[key] === undefined) {
        delete dbData[key];
      }
    });
    
    const { data, error } = await supabase
      .from('communication_templates')
      .upsert(dbData, { 
        onConflict: 'id',
        ignoreDuplicates: false
      })
      .select()
      .single();
    
    if (error) {
      console.error('Error saving template:', error);
      console.error('Template data:', dbData);
      
      // Provide more specific error feedback
      if (error.code === '23505') {
        console.error('Duplicate template detected');
      } else if (error.code === '42P01') {
        console.error('Table does not exist');
      }
      
      return null;
    }
    
    return mapDbToTemplate(data);
  } catch (error) {
    console.error('Error in saveTemplate:', error);
    return null;
  }
};

/**
 * Delete a template by ID
 */
export const deleteTemplate = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('communication_templates')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error('Error deleting template:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error in deleteTemplate:', error);
    return false;
  }
};

/**
 * Increment the usage count for a template
 */
export const incrementTemplateUsage = async (id: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase.rpc('increment_template_usage', { template_id: id });
    
    if (error) {
      console.error('Error incrementing template usage:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error in incrementTemplateUsage:', error);
    return false;
  }
};
